import {
  Comment,
  DateRange,
  Event,
  EventRepeatOutlined,
  Filter9Plus,
  Groups,
  ManageAccounts,
  Queue,
  Settings,
  Wifi,
  WifiOff,
} from "@mui/icons-material";
import BadgeIcon from "@mui/icons-material/Badge";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import CssBaseline from "@mui/material/CssBaseline";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import React from "react";
import { Offline, Online } from "react-detect-offline";
import { useAuth } from "react-oidc-context";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import CuressDark from "../assets/logo-dark.svg";
import CuressLight from "../assets/logo-white.svg";
import useDarkMode from "../hooks/useDarkMode";
import NotificationsPopOver from "./NotificationsPopOver";
import ProfileButton from "./ProfileButton";
import SettingsDrawer from "./SettingsDrawer";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import {
  useGetAllCaseloadContinuationsAggregateLazyQuery,
  useGetAllCaseloadContinuationsAggregateQuery,
} from "@src/graphql/generated/CaseloadContinuation.queries.generated";
import NotificationPopover from "./notifications/NotificationPopover";
import RuleIcon from "@mui/icons-material/Rule";

const drawerWidth = 195;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const mainRoles = ["hulpverlener", "planner", "team_coordinator", "read_only"];

const Layout = (props: { window: any }) => {
  const { isDarkMode } = useDarkMode();
  const location = useLocation();
  const { user } = useAuth();

  const user_role = user?.profile?.groups?.find((role) =>
    mainRoles.includes(role)
  );

  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const { data } = useGetAllCaseloadContinuationsAggregateQuery({});

  const drawer = (
    <div>
      <Toolbar />
      <Divider />

      <List>
        <ListSubheader
          component="div"
          sx={{
            bgcolor: "background.paper",
            fontWeight: "bold",
            fontSize: "1.1rem",
            lineHeight: 2,
          }}
        >
          Algemeen
        </ListSubheader>
        {(user_role == "admin" ||
          user_role == "planner" ||
          user_role == "read_only" ||
          user_role == "team_coordinator") && (
          <ListItem
            selected={location.pathname.includes("/teams")}
            button
            onClick={() => navigate("/teams")}
          >
            <ListItemIcon>
              <Groups
                color={
                  location.pathname.includes("/teams") ? "primary" : "inherit"
                }
              />
            </ListItemIcon>
            <ListItemText primary="Teams" />
          </ListItem>
        )}

        {(user_role == "admin" || user_role == "hulpverlener") && (
          <ListItem
            selected={location.pathname.includes("/caseload")}
            button
            onClick={() => navigate("/caseload")}
          >
            <ListItemIcon>
              <DateRange
                color={
                  location.pathname.includes("/caseload")
                    ? "primary"
                    : "inherit"
                }
              />
            </ListItemIcon>
            <ListItemText primary="Mijn caseload" />
          </ListItem>
        )}
        {(user_role == "admin" || user_role == "team_coordinator") && (
          <>
            <ListItem
              selected={location.pathname.includes("/vervolg")}
              button
              onClick={() => navigate("/vervolg")}
            >
              <ListItemIcon>
                <Badge
                  badgeContent={
                    data
                      ? data.case_load_continuation_aggregate.aggregate?.count
                      : ""
                  }
                  color="secondary"
                >
                  <Event
                    color={
                      location.pathname.includes("/vervolg")
                        ? "primary"
                        : "inherit"
                    }
                  />
                </Badge>
              </ListItemIcon>
              <ListItemText primary="Vervolg casus" />
            </ListItem>
            <ListItem
              selected={location.pathname.includes("/planner-opmerkingen")}
              button
              onClick={() => navigate("/planner-opmerkingen")}
            >
              <ListItemIcon>
                <Comment
                  color={
                    location.pathname.includes("/planner-opmerkingen")
                      ? "primary"
                      : "inherit"
                  }
                />
              </ListItemIcon>
              <ListItemText primary="Opmerkingen planner" />
            </ListItem>
          </>
        )}
        {(user_role == "admin" ||
          user_role == "planner" ||
          user_role == "read_only") && (
          <>
            <ListItem
              selected={location.pathname.includes("/allteams")}
              button
              onClick={() => navigate("/allteams")}
            >
              <ListItemIcon>
                <Filter9Plus
                  color={
                    location.pathname.includes("/allteams")
                      ? "primary"
                      : "inherit"
                  }
                />
              </ListItemIcon>
              <ListItemText primary="Alle caseloads" />
            </ListItem>
            <ListItem
              selected={location.pathname.includes("/clienten")}
              button
              onClick={() => navigate("/clienten")}
            >
              <ListItemIcon>
                <BadgeIcon
                  color={
                    location.pathname.includes("/clienten")
                      ? "primary"
                      : "inherit"
                  }
                />
              </ListItemIcon>
              <ListItemText primary="Cliënten" />
            </ListItem>
            <ListItem
              selected={location.pathname.includes("/gebruikers")}
              button
              onClick={() => navigate("/gebruikers")}
            >
              <ListItemIcon>
                <ManageAccounts
                  color={
                    location.pathname.includes("/gebruikers")
                      ? "primary"
                      : "inherit"
                  }
                />
              </ListItemIcon>
              <ListItemText primary="Gebruikers" />
            </ListItem>
            <ListItem
              selected={location.pathname.includes("/caseloadruimte")}
              button
              onClick={() => navigate("/caseloadruimte")}
            >
              <ListItemIcon>
                <Event
                  color={
                    location.pathname.includes("/caseloadruimte")
                      ? "primary"
                      : "inherit"
                  }
                />
              </ListItemIcon>
              <ListItemText primary="Caseload ruimte" />
            </ListItem>
            <ListItem
              selected={location.pathname.includes("/wachtlijst")}
              button
              onClick={() => navigate("/wachtlijst")}
            >
              <ListItemIcon>
                <Queue
                  color={
                    location.pathname.includes("/wachtlijst")
                      ? "primary"
                      : "inherit"
                  }
                />
              </ListItemIcon>
              <ListItemText primary="Aanmeldlijst" />
            </ListItem>
            <ListItem
              selected={location.pathname.includes("/all-planner-comments")}
              button
              onClick={() => navigate("/all-planner-comments")}
            >
              <ListItemIcon>
                <Comment
                  color={
                    location.pathname.includes("/all-planner-comments")
                      ? "primary"
                      : "inherit"
                  }
                />
              </ListItemIcon>
              <ListItemText primary="Planner opmerkingen" />
            </ListItem>
            <ListSubheader
              component="div"
              sx={{
                marginTop: 5,
                bgcolor: "background.paper",
                fontWeight: "bold",
                fontSize: "1.1rem",
                lineHeight: 2,
              }}
            >
              Systeem
            </ListSubheader>
            <ListItem
              selected={location.pathname.includes("/settings")}
              button
              onClick={() => navigate("/settings")}
            >
              <ListItemIcon>
                <Settings
                  color={
                    location.pathname.includes("/settings")
                      ? "primary"
                      : "inherit"
                  }
                />
              </ListItemIcon>
              <ListItemText primary="Instellingen" />
            </ListItem>
            <ListItem
              selected={location.pathname.includes("/data")}
              button
              onClick={() => navigate("/data")}
            >
              <ListItemIcon>
                <EqualizerIcon
                  color={
                    location.pathname.includes("/data") ? "primary" : "inherit"
                  }
                />
              </ListItemIcon>
              <ListItemText primary="Data" />
            </ListItem>
          </>
        )}
      </List>
    </div>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <img src={isDarkMode ? CuressDark : CuressLight} height={20} alt="" />
          {/* <Typography variant="h5" sx={{marginLeft: 'auto'}}>TEST OMGEVING</Typography> */}
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: 10,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Online>
              <Tooltip arrow title="Online!">
                <IconButton color="inherit">
                  <Wifi />
                </IconButton>
              </Tooltip>
            </Online>
            <Offline>
              <Tooltip arrow title="Offline!">
                <IconButton color="inherit">
                  <Badge
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    badgeContent={"!"}
                    color="error"
                  >
                    <WifiOff />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Offline>
            <NotificationPopover />
            {/* <NotificationsPopOver /> */}
            <SettingsDrawer />

            <ProfileButton />
          </div>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="folders"
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          overflow: "hidden",
        }}
      >
        <DrawerHeader />
        <Outlet />
      </Box>
    </Box>
  );
};

Layout.propTypes = {
  window: PropTypes.func,
};

export default Layout;
